import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet, apiPost } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';
import {
  Phone, ShieldCheck, AlertTriangle, ArrowLeft, Loader2, Search, Clock, Check,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchBrand } from '../api/a2p.js';

// Toll-Free provisioning flow (PR #429B). Separate from the local-number
// wizard because Toll-Free is VOICE-ONLY in CIVIX: it's for a public inbound
// main line, never for outbound dialing/texting. The number cannot send SMS
// and cannot do STIR/SHAKEN; instead it requires a Toll-Free Verification
// (TFV) that Twilio/carriers approve over 2-6 weeks.
export default function PhoneSetupTollFree() {
  const nav = useNavigate();
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [formError, setFormError] = useState('');

  // Business Profile must be complete before TFV can be submitted.
  const { data: brandData } = useQuery({
    queryKey: ['a2p-brand'],
    queryFn: () => fetchBrand().catch(() => ({ ok: false })),
  });
  const brand = brandData?.brand || null;
  const addr = brand?.address || {};
  const profileComplete = !!(brand?.legalBusinessName && brand?.ein && addr.street && addr.city && addr.state);

  async function runSearch() {
    setFormError('');
    setSearching(true);
    try {
      const r = await apiGet('/phones/search', { numberType: 'tollFree', limit: 20 });
      setResults(r.numbers || []);
      if (!r.numbers?.length) setFormError('No toll-free numbers available right now. Try again shortly.');
    } catch (e) {
      setFormError(e.response?.data?.error || e.message || 'Search failed.');
    } finally {
      setSearching(false);
    }
  }

  async function submitTfv() {
    if (!selected) return;
    setFormError('');
    setSubmitting(true);
    try {
      const r = await apiPost('/phones/purchase-toll-free', { phoneNumber: selected.phoneNumber });
      setSubmitted(r);
      toast.success('Toll-Free number acquired — verification submitted.');
    } catch (e) {
      const data = e.response?.data || {};
      setFormError(data.error || 'Could not submit Toll-Free Verification.');
      toast.error(data.error || 'Toll-Free request failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4">
      <TopBar title="Toll-Free Setup" subtitle="Communications" />

      <button onClick={() => nav('/phone-setup')} className="btn-ghost inline-flex items-center gap-1 text-sm">
        <ArrowLeft className="w-4 h-4" /> Back to Phone Setup
      </button>

      <div role="note" className="panel p-4 border border-amber-500/40 bg-amber-500/10 space-y-1.5">
        <div className="flex items-center gap-2 font-display font-bold text-amber-200">
          <AlertTriangle className="w-4 h-4" /> Toll-Free is voice-only in CIVIX
        </div>
        <ul className="text-xs text-amber-100/90 space-y-1">
          <li>SMS is blocked on Toll-Free numbers — use a Local number for outbound texting.</li>
          <li>Best for a public main-line number for inbound calls.</li>
          <li>Cannot do STIR/SHAKEN attestation (Toll-Free tech limit).</li>
          <li>Requires Toll-Free Verification — approval typically takes 2-6 weeks.</li>
        </ul>
      </div>

      {submitted ? (
        <div className="panel p-5 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-500/20 inline-flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-300" />
            </div>
            <div>
              <div className="font-display font-bold text-lg">Pending verification (2-6 weeks)</div>
              <div className="text-xs text-white/55">
                {submitted.phoneNumber?.number} is live for inbound voice. Toll-Free Verification has been submitted to Twilio.
              </div>
            </div>
          </div>
          <ul className="p-4 rounded-md border border-civix-border/50 bg-black/20 text-sm space-y-2">
            <li className="flex items-center gap-2 text-emerald-300"><Check className="w-4 h-4" /> Number acquired (voice-only)</li>
            <li className="flex items-center gap-2 text-emerald-300"><Check className="w-4 h-4" /> Toll-Free Verification submitted</li>
            <li className="flex items-center gap-2 text-white/60"><Loader2 className="w-4 h-4 animate-spin" /> Carrier approval — 2-6 weeks</li>
          </ul>
          <button onClick={() => nav('/phone-setup')} className="btn-primary inline-flex items-center gap-2">
            <Phone className="w-4 h-4" /> Done
          </button>
        </div>
      ) : !profileComplete ? (
        <div className="panel p-5 space-y-3">
          <div className="font-display font-bold text-lg">Business Profile required</div>
          <p className="text-sm text-white/70">
            Toll-Free Verification requires a complete Business Profile (legal name, EIN, business address).
            Finish it before requesting a Toll-Free number.
          </p>
          <button onClick={() => nav('/phone-setup')} className="btn-primary inline-flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" /> Go to Business Profile
          </button>
        </div>
      ) : (
        <div className="panel p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display font-bold text-lg">Find a Toll-Free number</div>
              <div className="text-xs text-white/55">We'll fetch available 8XX numbers from Twilio.</div>
            </div>
            <button onClick={runSearch} disabled={searching} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {searching ? 'Searching…' : 'Search'}
            </button>
          </div>

          {formError && (
            <div role="alert" className="p-3 rounded-md border border-rose-500/50 bg-rose-500/10 text-sm text-rose-200">{formError}</div>
          )}

          {results.length > 0 && (
            <ul role="radiogroup" className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {results.map((n) => {
                const isSel = selected?.phoneNumber === n.phoneNumber;
                return (
                  <li key={n.phoneNumber}>
                    <label role="radio" aria-checked={isSel} className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer ${isSel ? 'border-amber-400/60 bg-amber-500/10' : 'border-civix-border/50 hover:border-amber-400/40'}`}>
                      <input type="radio" name="tf-number" className="accent-civix-purple" checked={isSel} onChange={() => setSelected(n)} />
                      <span className="font-mono text-sm">{n.friendlyName || n.phoneNumber}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="flex justify-end">
            <button onClick={submitTfv} disabled={!selected || submitting} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              {submitting ? 'Submitting…' : 'Acquire & submit verification'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
